/**
 * Client speech-to-text for auto-host voice mode (#91, D2).
 *
 * When a player buzzes in, their browser captures the spoken answer and transcribes
 * it locally over the Web Speech `SpeechRecognition` API (webkit-prefixed on
 * Chromium). Only the resulting **transcript** ever leaves the browser
 * (`submit_spoken_answer`) — raw audio is never uploaded or stored (§9 privacy).
 *
 * Behind the small {@link SpeechRecognizer} interface so a backend STT (Transcribe)
 * is an additive swap. The browser impl feature-detects the constructor; where it is
 * absent (Firefox, jsdom under test) or the mic is denied, the buzz winner falls back
 * to a typed answer that submits the very same command (§5.3) — the mode stays
 * playable on any browser.
 */

import { isBackendSttEnabled, registerVoiceSingletonReset } from './voice-config';
import { TranscribeRecognizer } from './transcribe-recognizer';

/** A live transcription tick. `isFinal` marks a settled phrase vs an interim guess. */
export interface RecognitionResult {
  transcript: string;
  isFinal: boolean;
}

export interface RecognitionHandlers {
  /** Live transcript (final-so-far + current interim), for the on-screen readout. */
  onResult: (result: RecognitionResult) => void;
  /** Recognition ended; carries the settled final transcript. */
  onEnd: (finalTranscript: string) => void;
  /** A recognition error (e.g. `not-allowed` when the mic is denied). */
  onError?: (error: string) => void;
}

/** The seam every page reads STT through (browser today, backend-swappable). */
export interface SpeechRecognizer {
  /** True iff speech recognition is available in this environment. */
  isSupported(): boolean;
  /** Begin capturing; results stream to the handlers. No-op if unsupported. */
  start(handlers: RecognitionHandlers): void;
  /** Stop capturing and flush the final transcript (fires `onEnd`). */
  stop(): void;
  /** Stop capturing and discard (no `onEnd`) — used on teardown. */
  abort(): void;
}

// --- Structural subset of the (non-standardized) Web Speech recognition types. ---

interface RecognitionAlternativeLike {
  transcript: string;
}
interface RecognitionResultLike {
  readonly length: number;
  readonly isFinal: boolean;
  [index: number]: RecognitionAlternativeLike;
}
interface RecognitionResultListLike {
  readonly length: number;
  [index: number]: RecognitionResultLike;
}
interface RecognitionEventLike {
  resultIndex: number;
  results: RecognitionResultListLike;
}
interface RecognitionErrorEventLike {
  error: string;
}
export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: RecognitionEventLike) => void) | null;
  onerror: ((event: RecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}

export type RecognitionFactory = () => SpeechRecognitionLike;

function resolveGlobalFactory(): RecognitionFactory | null {
  const g = globalThis as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = g.SpeechRecognition ?? g.webkitSpeechRecognition;
  return Ctor ? () => new Ctor() : null;
}

export interface WebSpeechRecognizerOptions {
  /** Recognition constructor. Defaults to `window.SpeechRecognition`/webkit-prefixed. */
  factory?: RecognitionFactory | null;
  /** BCP-47 language tag. Defaults to `en-US`. */
  lang?: string;
}

/** Browser {@link SpeechRecognizer} over `SpeechRecognition`/`webkitSpeechRecognition`. */
export class WebSpeechRecognizer implements SpeechRecognizer {
  private readonly factory: RecognitionFactory | null;
  private readonly lang: string;
  private recognition: SpeechRecognitionLike | null = null;
  private finalTranscript = '';

  constructor(opts: WebSpeechRecognizerOptions = {}) {
    this.factory = opts.factory !== undefined ? opts.factory : resolveGlobalFactory();
    this.lang = opts.lang ?? 'en-US';
  }

  isSupported(): boolean {
    return this.factory !== null;
  }

  start(handlers: RecognitionHandlers): void {
    if (!this.factory) return; // unsupported → caller shows the typed fallback (§5.3)
    // Drop any prior session so a restart never double-appends.
    this.teardown();
    this.finalTranscript = '';

    const recognition = this.factory();
    this.recognition = recognition;
    recognition.lang = this.lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result) continue;
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) this.finalTranscript += text;
        else interim += text;
      }
      const combined = `${this.finalTranscript}${interim}`.trim();
      handlers.onResult({ transcript: combined, isFinal: false });
    };
    recognition.onerror = (event) => {
      handlers.onError?.(event.error);
    };
    recognition.onend = () => {
      const settled = this.finalTranscript.trim();
      this.recognition = null;
      handlers.onEnd(settled);
    };

    recognition.start();
  }

  stop(): void {
    // Fires onend with whatever has been transcribed so far.
    this.recognition?.stop();
  }

  abort(): void {
    const recognition = this.recognition;
    if (!recognition) return;
    // Detach handlers first so abort() doesn't fire a spurious onEnd on teardown.
    recognition.onend = null;
    recognition.onresult = null;
    recognition.onerror = null;
    this.recognition = null;
    recognition.abort();
  }

  private teardown(): void {
    if (this.recognition) this.abort();
  }
}

let sharedRecognizer: SpeechRecognizer | null = null;
let backendRecognizer: SpeechRecognizer | null = null;

// A voice-config change (config fetch / test reset) drops the cached singletons so
// the next `get` re-selects the correct adapter.
registerVoiceSingletonReset(() => {
  sharedRecognizer = null;
  backendRecognizer = null;
});

/**
 * The process-wide recognizer. Returns the backend {@link TranscribeRecognizer} when
 * the deployment advertised backend STT (#93, D-SEAM) — giving spoken-answer capture
 * on browsers without the Web Speech recognition API (Firefox, Safari/iOS) — else the
 * browser {@link WebSpeechRecognizer}.
 */
export function getSpeechRecognizer(): SpeechRecognizer {
  if (isBackendSttEnabled()) {
    if (!backendRecognizer) {
      // Wire a browser fallback (D-FALL middle tier) so a backend STT failure lands on
      // the Web Speech recognizer before the typed fallback, mirroring the synth factory.
      backendRecognizer = new TranscribeRecognizer({
        fallback: new WebSpeechRecognizer(),
      });
    }
    return backendRecognizer;
  }
  if (!sharedRecognizer) sharedRecognizer = new WebSpeechRecognizer();
  return sharedRecognizer;
}
